import { graphql } from 'gatsby';
import { kebabCase } from 'lodash';
import React, { FC } from 'react';

import {
  Adimo,
  check3rdPartyAvailability,
  MikMak,
  PriceSpider,
  PriceSpiderModel,
} from '~/3rdPartyIntegrations';
import BodyRender from '~/components/BodyRender';
import { BodyRenderExternalData } from '~/components/BodyRender/models';
import { RedirectsProvider } from '~/context/redirects';
import { Layout } from '~/layout/Layout';
import { addBrandPostfixConditionally } from '~/utils/addBrandPostfixConditionally';
import withContentstackPreview from '~/utils/livePreview';

import { ContentPageData } from './models';

const BASE_CLASS = 'l2-content-page';

export const L2ContentPage: FC<ContentPageData> = ({
  data: {
    profile,
    contentstackL2ContentPage,
    contentstackHeader,
    contentstackHeaderWithSplitter,
    contentstackFooter,
    contentstackFooterWithSplitter,
    contentstackInfoBar,
    allContentstackItemListingGrid: { nodes: itemListingGrids },
    allContentstackAssets: { nodes: jsonRTEImages },
    contentstackSettings,
  },
  pageContext: { uid, locale, seoTitle, brand, fullPath, redirects },
}) => {
  if (typeof window === 'undefined')
    console.info('Building:', JSON.stringify({ uid, locale, seoTitle }));

  const { seo, pageHeader, fields, body, categories, openGraph } =
    contentstackL2ContentPage;

  const categoryClasses = categories.map(
    ({ title }) => `${BASE_CLASS}--${kebabCase(title)}`
  );
  const mainImagePath =
    pageHeader.imageElement[0]?.image &&
    'url' in pageHeader.imageElement[0].image
      ? pageHeader.imageElement[0].image.url
      : undefined;

  const { priceSpider, adimo, shoppablePlugin, pluginInformationText, mikmak } =
    contentstackSettings;

  const {
    isPriceSpiderAvailable,
    isAdimoAvailable,
    isMikMakV1Available,
    isMikMakV2Available,
  } = check3rdPartyAvailability(shoppablePlugin, priceSpider, adimo, mikmak);

  const externalData: BodyRenderExternalData = {
    locale,
    itemListingGrids,
    jsonRTEImages,
    whereToBuyData: {
      isPriceSpiderAvailable,
      isAdimoAvailable,
      adimoKey: adimo?.key,
      adimoButton: adimo?.buttonText,
      isMikMakV1Available,
      isMikMakV2Available,
      mikmakAppId: mikmak?.appId,
      mikmakWidgetId: mikmak?.widgetId,
      mikmakButton: mikmak?.buttonText,
      shoppableInformationText: pluginInformationText,
    },
  };

  return (
    <RedirectsProvider redirects={redirects}>
      <Layout
        {...{
          openGraph,
          mainImagePath,
          seo,
          headerBasic: contentstackHeader,
          headerWithSplitter: contentstackHeaderWithSplitter,
          headerSplitterAdditionalData: {
            externalData,
            currentPageUid: uid,
          },
          footerBasic: contentstackFooter,
          footerWithSplitter: contentstackFooterWithSplitter,
          footerSplitterAdditionalData: {
            externalData,
            currentPageUid: uid,
          },
          pageUid: uid,
          locale,
          brand,
          pageHeaderProps: { pageHeader, fields },
          className: [BASE_CLASS, ...categoryClasses].join(' '),
          infoBar: {
            ...contentstackInfoBar,
          },
          fullPath,
          profile,
        }}
      >
        {isPriceSpiderAvailable ? (
          <PriceSpider
            priceSpider={priceSpider as PriceSpiderModel}
            lang={locale}
          />
        ) : null}
        {isAdimoAvailable ? <Adimo url={adimo!.url!} /> : null}
        {isMikMakV1Available || isMikMakV2Available ? (
          <MikMak appId={mikmak?.appId!} />
        ) : null}
        <BodyRender
          items={body}
          externalData={externalData}
          currentPageUid={uid}
        />
      </Layout>
    </RedirectsProvider>
  );
};

export const query = graphql`
  query L2ContentPage(
    $id: String
    $locale: String
    $brand: [String]
    $jsonRTEImgUids: [String]
  ) {
    profile: contentstackProfile {
      ...ProfileFragment
    }
    contentstackL2ContentPage(id: { eq: $id }) {
      uid
      updated_at
      publish_details {
        time
      }
      fields {
        ...PageFieldsFragment
      }
      seo {
        ...SeoFragment
      }
      openGraph: open_graph {
        ...OpenGraphFragment
      }
      pageHeader: page_header {
        ...PageHeaderFragment
      }
      body {
        ...BodyFragment
      }
      categories {
        title
      }
    }
    allContentstackItemListingGrid {
      nodes {
        ...ItemListingGridFragment
      }
    }
    allContentstackAssets(filter: { uid: { in: $jsonRTEImgUids } }) {
      nodes {
        uid
        url
        gatsbyImageData(layout: FULL_WIDTH, quality: 80)
      }
    }
    contentstackHeader(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderBasicFragment
    }
    contentstackHeaderWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...HeaderWithSplitterFragment
    }
    contentstackSettings(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...SettingsFragment
    }
    contentstackInfoBar(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...InfoBarFragment
    }
    contentstackFooter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterBasicFragment
    }
    contentstackFooterWithSplitter(
      publish_details: { locale: { eq: $locale } }
      tags: { in: $brand }
    ) {
      ...FooterWithSplitterFragment
    }
  }
`;

export default withContentstackPreview(
  L2ContentPage,
  addBrandPostfixConditionally(
    'l2_content_page',
    process.env.GATSBY_CONTENTSTACK_BRAND_TAG
  )
);

export { Head } from '~/common/StructuredData';
